import React, { useEffect, useRef, useState, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';

import { includesConditionOptions } from 'constants/automation';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { useGlobalTagList } from 'hooks/useGlobalTags';

const TagFilter = ({ index, practiceAreaId, globalList }) => {
  const { formattedGlobalTag, isTagsLoading } = useGlobalTagList(practiceAreaId);
  const {
    formState: { errors },
    control,
    getValues,
    setValue,
  } = useFormContext();
  const isFirstRender = useRef(true);

  const [optionsVersion, setOptionsVersion] = useState(0);
  const [listVersion, setListVersion] = useState(0);
  const prevGlobalList = useRef(globalList);
  const prevFormattedGlobalTag = useRef(formattedGlobalTag);

  useEffect(() => {
    if (globalList !== prevGlobalList.current) {
      if (globalList !== undefined && globalList !== null && Array.isArray(globalList)) {
        setOptionsVersion((prev) => prev + 1);
      }
      prevGlobalList.current = globalList;
    }
  }, [globalList]);

  useEffect(() => {
    if (formattedGlobalTag !== prevFormattedGlobalTag.current) {
      if (formattedGlobalTag !== undefined && formattedGlobalTag !== null && Array.isArray(formattedGlobalTag)) {
        setListVersion((prev) => prev + 1);
      }
      prevFormattedGlobalTag.current = formattedGlobalTag;
    }
  }, [formattedGlobalTag]);

  const options = useMemo(() => {
    const optionsHasUpdates = optionsVersion > 0;
    const listHasUpdates = listVersion > 0;

    if (optionsHasUpdates && listHasUpdates) {
      return optionsVersion >= listVersion ? globalList || [] : formattedGlobalTag || [];
    } else if (optionsHasUpdates) {
      return globalList || [];
    } else if (listHasUpdates) {
      return formattedGlobalTag || [];
    }
    return formattedGlobalTag || [];
  }, [globalList, formattedGlobalTag, optionsVersion, listVersion]);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    const currentValue = getValues(`filters.${index}.value`);
    const selectedValues = currentValue?.['lead_details.tags'] || [];

    if (selectedValues.length > 0 && options.length > 0) {
      const validOptionValues = new Set(options.map((option) => option.value));
      const validValues = selectedValues.filter((value) => validOptionValues.has(value));

      if (validValues.length !== selectedValues.length) {
        setValue(`filters.${index}.value`, { 'lead_details.tags': validValues }, { shouldValidate: true });
      }
    }
    if (options.length === 0) {
      setValue(`filters.${index}.value`, { 'lead_details.tags': [] }, { shouldValidate: true });
    }
  }, [options, index, getValues, setValue]);

  return (
    <>
      <div className="me-2 filterbar-condition">
        <Controller
          name={`filters.${index}.condition`}
          control={control}
          rules={{ required: 'Condition is required' }}
          render={({ field }) => (
            <Dropdown
              options={includesConditionOptions}
              value={field.value}
              onChange={(e) => field.onChange(e.value)}
              placeholder="Select Condition"
              className="w-100"
              inputRef={field.ref}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.condition`} errors={errors} />
      </div>
      <div className="me-2 filterbar-value">
        <Controller
          name={`filters.${index}.value`}
          control={control}
          rules={{ required: 'Value is required' }}
          render={({ field }) => (
            <MultiSelect
              options={options}
              value={(field.value?.['lead_details.tags'] || []).filter((selected) => options.some((option) => option.value === selected))}
              name={field.name}
              onChange={(e) => field.onChange({ 'lead_details.tags': e.value })}
              filter
              className="input-shadow w-100"
              display="chip"
              placeholder="Select"
              optionLabel="name"
              optionValue="value"
              dropdownIcon={isTagsLoading ? 'pi pi-spin pi-spinner' : undefined}
            />
          )}
        />
        <RequiredValidation field={`filters.${index}.value`} errors={errors} />
      </div>
    </>
  );
};

export default TagFilter;
